// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  getPerformanceLineLabel,
  getTranslatedChartLabel,
} from './get-performance-line-label';
import { ResponsiveLine } from '@nivo/line';
import { type Backtest } from '@shared/data-access/interfaces/api/ai/portfolio';
import { type Ts } from '@shared/data-access/interfaces/api/commons';
import { ChartContainer, Legend } from '@shared/ui-kit/components';
import { transformAssetsTsData, transformTsData } from '@shared/ui-kit/utils';
import {
  type FC,
  type MouseEventHandler,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { PerformanceLineChartTooltip } from './performance-line-chart-tooltip';
import { useTranslation } from 'react-i18next';

export type PerformanceLineChartFeature =
  | 'aggregator'
  | 'rebalance'
  | 'agg-returns';

export interface PerformanceLineChartProps {
  data: Ts | Array<Backtest & { ticker: string }>;
  isReturns: boolean;
  name: string;
  crossType?: 'bottom' | 'top';
  enableArea?: boolean;
  benchmark?: string;
  feature?: PerformanceLineChartFeature;
}

export const PerformanceLineChart: FC<PerformanceLineChartProps> = ({
  data,
  isReturns,
  name,
  crossType = 'bottom',
  enableArea = true,
  benchmark = '',
  feature,
}) => {
  const { t } = useTranslation();
  const [activeSeries, setActiveSeries] = useState<Record<string, boolean>>(
    () => ({}),
  );
  const BENCHMARK_COLOR = '#79828D80';
  const PORTFOLIO_COLOR = '#1072BA';

  const getSerieColor = (id: string) => {
    const normalizedId = id.toUpperCase();

    if (normalizedId.includes('BENCHMARK')) {
      return BENCHMARK_COLOR;
    }

    if (
      normalizedId.includes('PORTFOLIO') ||
      normalizedId.includes('TARGET') ||
      normalizedId.includes('CURRENT')
    ) {
      return PORTFOLIO_COLOR;
    }

    return BENCHMARK_COLOR;
  };

  const chartData = useMemo(() => {
    return (
      Array.isArray(data)
        ? transformAssetsTsData(data, benchmark)
        : transformTsData(data)
    ).map(serie => ({
      ...serie,
      id: getPerformanceLineLabel(serie.id, feature as string),
      color: getSerieColor(serie.id),
    }));
  }, [benchmark, data, feature]);

  const getTranslatedLabel = useCallback(
    (id: string): string => getTranslatedChartLabel(id, t, feature),
    [t, feature],
  );

  const filteredData = useMemo(
    /* istanbul ignore next */
    () => chartData.filter(data => !activeSeries?.[data.id]),
    [activeSeries, chartData],
  );

  const areaBaseLine = useMemo(
    () =>
      isReturns
        ? Array.isArray(data)
          ? Math.min(
              ...data.flatMap(({ ts_backtest }) =>
                ts_backtest.benchmark.concat(ts_backtest.portfolio),
              ),
            )
          : Math.min(...data.benchmark, ...data.portfolio) * 100
        : 0,
    [data, isReturns],
  );
  /* istanbul ignore next */
  const onToggleSerie: MouseEventHandler<HTMLButtonElement> = useCallback(e => {
    const serie = e.currentTarget.dataset['serie'];

    if (serie) {
      setActiveSeries(prev => ({
        ...prev,
        [serie]: !prev[serie],
      }));
    }
  }, []);
  /* istanbul ignore next */
  const legends = useMemo(
    () =>
      Array.from(chartData, ({ variation, id, color }) => (
        <Legend
          key={id}
          label={getTranslatedLabel(id)}
          color={!activeSeries[id] ? color : '#79828D'}
          variation={variation}
          onClick={onToggleSerie}
        />
      )),
    [activeSeries, chartData, onToggleSerie, getTranslatedLabel],
  );

  return (
    <ChartContainer
      header={name}
      popupName={
        isReturns
          ? feature === 'agg-returns'
            ? 'charts.aggregatorReturns'
            : 'charts.returns'
          : 'kpis.maxDrawdown'
      }
      tooltip
    >
      <div className="flex flex-wrap justify-end gap-x-7 gap-y-2">{legends}</div>
      <ResponsiveLine
        data={filteredData}
        margin={{ top: 50, right: 25, bottom: 50, left: 50 }}
        yScale={{
          type: 'linear',
          min: 'auto',
          max: 'auto',
          stacked: false,
          reverse: false,
          clamp: true,
          nice: false,
        }}
        yFormat=" >-.2f"
        curve="linear"
        axisTop={null}
        axisRight={null}
        animate={false}
        axisBottom={{
          tickSize: 0,
          tickPadding: 20,
          tickRotation: 0,
          tickValues: 7,
          legend: '',
          format: '%m-%Y',
        }}
        xFormat="time:%Y-%m-%d"
        xScale={{
          format: '%Y-%m-%d',
          precision: 'day',
          type: 'time',
          useUTC: false,
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 18,
          tickRotation: 0,
          legend: '',
        }}
        enableGridX={false}
        colors={{ datum: 'color' }}
        enablePoints={false}
        enableArea={enableArea}
        areaBaselineValue={areaBaseLine}
        areaOpacity={0.3}
        areaBlendMode="multiply"
        enableSlices={false}
        useMesh={true}
        theme={{
          axis: {
            ticks: {
              text: {
                fontSize: 12,
                fontFamily: 'poppins',
                fill: 'var(--border-dark)',
              },
            },
          },
          grid: {
            line: {
              stroke: 'var(--border-light)',
              strokeWidth: 1,
              strokeDasharray: '8 12',
            },
          },
        }}
        tooltip={PerformanceLineChartTooltip}
        crosshairType={crossType}
        defs={[
          {
            colors: [
              {
                color: 'inherit',
                offset: 30,
              },
              {
                color: 'inherit',
                offset: 100,
                opacity: 0.37,
              },
            ],
            id: 'gradientA',
            type: 'linearGradient',
          },
        ]}
        fill={[
          {
            id: 'gradientA',
            match: '*',
          },
        ]}
      />
    </ChartContainer>
  );
};
